import { Loader } from '@googlemaps/js-api-loader';
import { GoogleMapsOptions, Position } from './google-maps-options';

declare global {
  interface Window {
    appLocations?: {
      openLocationPopup?: (locationId: string | number) => void;
    };
  }
}

export function activeGoogleMapsMultipleMarkers({ apiKey, domId, zoom, positions, gestureHandling }: GoogleMapsOptions) {
  const loader = new Loader({ apiKey, version: 'weekly' });
  const safePositions = positions ?? [];

  const parseCoordinate = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const normalized = value.trim().replace(',', '.');
      if (!normalized) return null;
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const getLatLng = (item: Position): { lat: number; lng: number } | null => {
    const gps = item?.Gps as
      | { latitude?: unknown; longitude?: unknown; Latitude?: unknown; Longitude?: unknown }
      | undefined;

    if (!gps) return null;

    const lat = parseCoordinate(gps.latitude ?? gps.Latitude);
    const lng = parseCoordinate(gps.longitude ?? gps.Longitude);
    if (lat == null || lng == null) return null;

    return { lat, lng };
  };

  const validPositions = safePositions
    .map((item) => ({ item, latLng: getLatLng(item) }))
    .filter((entry): entry is { item: Position; latLng: { lat: number; lng: number } } => entry.latLng != null);

  const isLocked = gestureHandling === 'none';

  const mapOptions = {
    center: {
      lat: validPositions[0]?.latLng.lat ?? 0,
      lng: validPositions[0]?.latLng.lng ?? 0,
    },
    zoom,
    scrollwheel: false,
    zoomControl: true,
    gestureHandling: gestureHandling ?? 'auto',
    disableDefaultUI: isLocked,
    mapTypeId: 'roadmap',
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#fdf8ee' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#1a3a6e' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#fdf8ee' }] },
      { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#c9b47a' }] },
      { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#1a3a6e' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#d8ead3' }] },
      { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#4a7c59' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e8dfc0' }] },
      { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#1a3a6e' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#f5a800' }] },
      { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#d48f00' }] },
      { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#1a3a6e' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#b3d4f0' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#1a3a6e' }] },
    ],
  };

  loader.load().then((google) => {
    const map = new google.maps.Map(document.getElementById(domId), mapOptions);
    const bounds = new google.maps.LatLngBounds();
    const singlePos = validPositions.length === 1;

    validPositions.forEach(({ item, latLng }) => {
      const marker = new google.maps.Marker({
        position: latLng,
        map,
      });

      if (singlePos) {
        map.setCenter(marker.getPosition());
      } else {
        bounds.extend(marker.getPosition());
      }

      if (item.Id != null) {
        marker.addListener('click', () => {
          window.appLocations?.openLocationPopup?.(item.Id as number);
        });
      }
    });

    if (!singlePos) {
      map.fitBounds(bounds);
    }
  });
}